import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt    = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTs  = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const money  = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'
const moneyN = (n) => Number(n || 0).toFixed(2)

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()

const PAYOUT_LABELS = { mtn: 'MTN MoMo', airtel: 'Airtel Money', bank: 'Bank Account' }
const REQ_STATUS = {
  pending:  { color: '#b45309', bg: '#fffbeb', border: '#fde68a', label: 'Pending'  },
  paid:     { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: 'Paid'     },
  rejected: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca', label: 'Rejected' },
}

// ── Tier metadata (mirrors SellerPayment's TIER_META) ────────────────────────
const TIER_META = {
  bronze:   { label: 'Bronze',   icon: '🥉', color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  silver:   { label: 'Silver',   icon: '🥈', color: '#374151', bg: '#f3f4f6', border: '#d1d5db' },
  gold:     { label: 'Gold',     icon: '🥇', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  platinum: { label: 'Platinum', icon: '💎', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
}

const VERIF_LABEL = {
  verified:   'Verified',
  pending:    'Pending Review',
  rejected:   'REJECTED — Payout Blocked',
  unverified: 'UNVERIFIED — Payout Blocked',
  blocked:    'Blocked (Unverified + Rejected)',
}
const VERIF_COLOR  = { verified: '#15803d', pending: '#1d4ed8', rejected: '#b91c1c', unverified: '#92400e', blocked: '#b91c1c' }
const VERIF_BG     = { verified: '#d1fae5', pending: '#dbeafe', rejected: '#fee2e2', unverified: '#fef3c7', blocked: '#fee2e2' }
const VERIF_BORDER = { verified: '#6ee7b7', pending: '#93c5fd', rejected: '#fca5a5', unverified: '#fcd34d', blocked: '#fca5a5' }

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS — Earnings Report (period-based)
// ═══════════════════════════════════════════════════════════════════════════
function exportReportPDF(creators, period, commPct = 0, filters = {}) {
  const filterNote = []
  if (filters.verification) filterNote.push(`Verification: ${VERIF_LABEL[filters.verification] || filters.verification}`)
  if (filters.category)     filterNote.push(`Category: ${filters.category}`)
  if (filters.search)       filterNote.push(`Search: "${filters.search}"`)

  const sumGross = creators.reduce((a, c) => a + Number(c.gross_total || 0), 0)
  const sumFee   = creators.reduce((a, c) => a + Number(c.fee_total   || 0), 0)
  const sumNet   = creators.reduce((a, c) => a + Number(c.net_total   || 0), 0)
  const sumTips  = creators.reduce((a, c) => a + Number(c.tips_count  || 0), 0)
  const sumVideos = creators.reduce((a, c) => a + Number(c.video_count || 0), 0)
  const sumPasses = creators.reduce((a, c) => a + Number(c.pass_count || 0), 0)
  const blockedCount = creators.filter(c => ['unverified','rejected'].includes(c.verification_status)).length

  const rows = creators.map(c => {
    const vs      = c.verification_status ?? 'unverified'
    const blocked = ['unverified','rejected'].includes(vs)
    const rowBg   = blocked ? '#fff5f5' : (vs === 'verified' ? '#f0fdf4' : '#fff')
    return `<tr style="background:${rowBg}">
      <td><strong>${c.display_name || '—'}</strong><br><span style="color:#9a9a94;font-size:9px">${c.owner_phone || ''}</span></td>
      <td>${c.category || '—'}</td>
      <td>${c.owner_name || '—'}</td>
      <td style="text-align:center">${c.tips_count}</td>
      <td style="text-align:center">${c.video_count}</td>
      <td style="text-align:center">${c.pass_count || 0}</td>
      <td style="text-align:right;color:#374151">${moneyN(c.gross_total)}</td>
      <td style="text-align:right;color:#b91c1c">${moneyN(c.fee_total)}</td>
      <td style="text-align:right;color:#15803d;font-weight:900">${moneyN(c.net_total)}</td>
      <td style="color:${VERIF_COLOR[vs] || '#374151'};font-size:9px;font-weight:700">${blocked ? '⛔ ' : (vs === 'verified' ? '✓ ' : '')}${VERIF_LABEL[vs] || vs}</td>
    </tr>`
  }).join('')

  const vf = filters.verification
  const badgeHtml = vf
    ? `<span style="display:inline-block;padding:3px 10px;border-radius:4px;font-size:10px;font-weight:700;background:${VERIF_BG[vf]||'#f3f4f6'};color:${VERIF_COLOR[vf]||'#374151'};border:1px solid ${VERIF_BORDER[vf]||'#e5e7eb'};margin-bottom:10px">${VERIF_LABEL[vf] || vf}</span>`
    : ''

  const html = `<!DOCTYPE html><html><head><title>Kudos Creator Payouts — ${period}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:20px}
    h1{font-size:17px;font-weight:900;margin-bottom:4px}
    .meta{font-size:10px;color:#666;margin-bottom:8px;font-family:monospace}
    .warn{background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:7px 12px;font-size:10px;color:#b91c1c;margin-bottom:10px;font-weight:700}
    table{width:100%;border-collapse:collapse;font-size:10px}
    th{background:#f3f4f6;text-align:left;padding:6px 8px;font-size:8px;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;border-bottom:2px solid #e5e7eb}
    td{padding:6px 8px;border-bottom:1px solid #f3f4f6;vertical-align:top}
    .tfooter td{font-weight:700;background:#f9f9f7;border-top:2px solid #e5e7eb}
    @media print{body{padding:10px}@page{margin:15mm}}
  </style></head><body>
  <h1>Kudos Creator Earnings (Tips + Video Unlocks + Kudos Premium)</h1>
  <div class="meta">Period: ${period} · ${creators.length} creators · ${sumTips} tips · ${sumVideos} video unlocks · ${sumPasses} Kudos Premium payouts${filterNote.length ? ' · Filtered: ' + filterNote.join(' · ') : ''}</div>
  ${badgeHtml}
  ${blockedCount > 0 ? `<div class="warn">⛔ ${blockedCount} creator${blockedCount > 1 ? 's' : ''} have payout BLOCKED — do not process these payouts until verified.</div>` : ''}
  <table>
    <thead><tr>
      <th>Creator</th><th>Category</th><th>Owner</th><th>Tips</th><th>Videos</th><th>Premium</th>
      <th style="text-align:right">Gross (RWF)</th>
      <th style="text-align:right">Platform Fee (RWF)</th>
      <th style="text-align:right">Net Payout (RWF)</th>
      <th>Verification</th>
    </tr></thead>
    <tbody>${rows}
    <tr class="tfooter">
      <td colspan="3"><strong>TOTAL (${creators.length} creators)</strong></td>
      <td style="text-align:center">${sumTips}</td>
      <td style="text-align:center">${sumVideos}</td>
      <td style="text-align:center">${sumPasses}</td>
      <td style="text-align:right">${moneyN(sumGross)}</td>
      <td style="text-align:right;color:#b91c1c">${moneyN(sumFee)}</td>
      <td style="text-align:right;color:#15803d">${moneyN(sumNet)}</td>
      <td></td>
    </tr></tbody>
  </table>
  <div style="margin-top:14px;font-size:9px;color:#9a9a94;font-family:monospace">
    Generated ${new Date().toLocaleString('en-GB')} · RWF amounts · Platform commission ${(commPct*100).toFixed(0)}% base, discounted per creator tier · Kudos Premium earnings are pooled monthly by watch-time share
  </div>
  <script>window.onload=function(){window.print()}<\/script>
  </body></html>`

  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html)
  w.document.close()
}

function exportReportCSV(creators, period) {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const headers = ['Creator', 'Category', 'Owner', 'Phone', 'Verification', 'Payout Blocked', 'Tips Count', 'Video Unlocks Count', 'Kudos Premium Payouts Count', 'Gross (RWF)', 'Platform Fee (RWF)', 'Net Payout (RWF)', 'Pending Request (RWF)']
  const lines = creators.map(c => {
    const vs      = c.verification_status ?? 'unverified'
    const blocked = ['unverified','rejected'].includes(vs)
    return [
      c.display_name, c.category || '', c.owner_name || '', c.owner_phone || '',
      VERIF_LABEL[vs] || vs, blocked ? 'YES' : 'No',
      c.tips_count, c.video_count, c.pass_count || 0, moneyN(c.gross_total), moneyN(c.fee_total), moneyN(c.net_total),
      c.pending_request_id ? moneyN(c.pending_request_amount) : '',
    ].map(escape).join(',')
  })
  const csv  = [headers.join(','), ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-creator-earnings-${period}.csv`; a.click()
  URL.revokeObjectURL(url)
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS — Payout Requests (all-time queue)
// ═══════════════════════════════════════════════════════════════════════════
function exportRequestsCSV(requests, status) {
  const escape  = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const headers = ['Request ID', 'Creator', 'Owner', 'Phone', 'Amount (RWF)', 'Method', 'Bank Name', 'Account', 'Status', 'Requested', 'Resolved', 'Admin Note']
  const lines = requests.map(r => [
    r.request_id, r.display_name, r.owner_name || '', r.owner_phone || '',
    moneyN(r.amount), PAYOUT_LABELS[r.payout_method] || r.payout_method || '', r.bank_name || '', r.payout_account || '',
    REQ_STATUS[r.status]?.label || r.status, fmtTs(r.requested_at), r.resolved_at ? fmtTs(r.resolved_at) : '',
    r.admin_note || '',
  ].map(escape).join(','))
  const csv  = [headers.join(','), ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-creator-payout-requests-${status || 'all'}.csv`; a.click()
  URL.revokeObjectURL(url)
}

function exportRequestsPDF(requests, status) {
  const rows = requests.map(r => {
    const s = REQ_STATUS[r.status] || REQ_STATUS.pending
    return `<tr>
      <td><strong>${r.display_name || '—'}</strong><br><span style="color:#9a9a94;font-size:9px">${r.owner_phone || ''}</span></td>
      <td>${PAYOUT_LABELS[r.payout_method] || r.payout_method || '—'}${r.payout_method === 'bank' && r.bank_name ? `<br><span style="font-size:9px;color:#9a9a94">${r.bank_name}</span>` : ''}</td>
      <td>${r.payout_account || '—'}</td>
      <td style="text-align:right;font-weight:700">${moneyN(r.amount)}</td>
      <td style="color:${s.color};font-weight:700">${s.label}</td>
      <td>${fmtTs(r.requested_at)}</td>
      <td>${r.resolved_at ? fmtTs(r.resolved_at) : '—'}</td>
    </tr>`
  }).join('')
  const total = requests.reduce((a, r) => a + Number(r.amount || 0), 0)

  const html = `<!DOCTYPE html><html><head><title>Kudos Creator Payout Requests</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:20px}
    h1{font-size:17px;font-weight:900;margin-bottom:4px}
    .meta{font-size:10px;color:#666;margin-bottom:10px;font-family:monospace}
    table{width:100%;border-collapse:collapse;font-size:10px}
    th{background:#f3f4f6;text-align:left;padding:6px 8px;font-size:8px;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;border-bottom:2px solid #e5e7eb}
    td{padding:6px 8px;border-bottom:1px solid #f3f4f6;vertical-align:top}
    .tfooter td{font-weight:700;background:#f9f9f7;border-top:2px solid #e5e7eb}
    @media print{body{padding:10px}@page{margin:15mm}}
  </style></head><body>
  <h1>Kudos Creator Payout Requests</h1>
  <div class="meta">Filter: ${status ? REQ_STATUS[status]?.label || status : 'All'} · ${requests.length} request${requests.length !== 1 ? 's' : ''}</div>
  <table>
    <thead><tr><th>Creator</th><th>Method</th><th>Account</th><th style="text-align:right">Amount</th><th>Status</th><th>Requested</th><th>Resolved</th></tr></thead>
    <tbody>${rows}
    <tr class="tfooter"><td colspan="3"><strong>TOTAL</strong></td><td style="text-align:right">${moneyN(total)}</td><td colspan="3"></td></tr>
    </tbody>
  </table>
  <div style="margin-top:14px;font-size:9px;color:#9a9a94;font-family:monospace">Generated ${new Date().toLocaleString('en-GB')} · RWF amounts</div>
  <script>window.onload=function(){window.print()}<\/script>
  </body></html>`

  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html)
  w.document.close()
}

// ── Sub-components ────────────────────────────────────────────────────────────
function TierBadge({ tier, small }) {
  const m = TIER_META[tier] || TIER_META.bronze
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontSize: small ? 8 : 10, fontWeight: 700, padding: small ? '1px 5px' : '2px 7px',
      borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
      {m.icon} {m.label}
    </span>
  )
}

function SummaryCard({ label, value, sub, accent, bold }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: bold ? 22 : 20, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ height: 32, minWidth: 32, padding: '0 10px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#d1d5db' : '#374151', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'JetBrains Mono',monospace" }}>
      {label}
    </button>
  )
}

// ── Resolve Confirm Modal (shared by both tabs) ───────────────────────────────
function ResolveModal({ action, onClose, onConfirm, loading }) {
  if (!action) return null
  const { request, status } = action
  const paying = status === 'paid'
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>{paying ? '✅' : '⚠️'}</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>{paying ? 'Mark as Paid?' : 'Reject Payout Request?'}</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            {paying
              ? <>Confirms <b>{money(request.amount)}</b> was sent to <b>{request.display_name}</b> via {PAYOUT_LABELS[request.payout_method] || request.payout_method}. This cannot be undone.</>
              : <>The reserved tips for this request will become payable again, and <b>{request.display_name}</b> can submit a new request.</>}
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : paying ? '#15803d' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Saving…' : paying ? 'Yes, Mark Paid' : 'Yes, Reject'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Creator Earnings Drawer ───────────────────────────────────────────────────
function CreatorReportDrawer({ creator, dateFrom, dateTo, onClose, onResolve }) {
  const [tips,            setTips]            = useState([])
  const [videoPurchases,  setVideoPurchases]  = useState([])
  const [passEarnings,    setPassEarnings]    = useState([])
  const [totals,  setTotals]  = useState(null)
  const [tier,    setTier]    = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!creator) return
    setLoading(true)
    api.get(`/creators/admin/${creator.creator_id}/payout-report?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(res => {
        setTips(res.data.tips || [])
        setVideoPurchases(res.data.video_purchases || [])
        setPassEarnings(res.data.pass_earnings || [])
        setTotals(res.data.totals || null)
        setTier(res.data.tier || null)
      })
      .catch(() => { setTips([]); setVideoPurchases([]); setPassEarnings([]); setTotals(null); setTier(null) })
      .finally(() => setLoading(false))
  }, [creator, dateFrom, dateTo])

  if (!creator) return null
  const bg = avatarBg(creator.display_name || '')
  const vs = creator.verification_status ?? 'unverified'
  const blocked = ['unverified', 'rejected'].includes(vs)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 520, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, flexShrink: 0 }}>{initials(creator.display_name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{creator.display_name || '—'}</div>
            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              {creator.owner_name} · {creator.owner_phone} · {creator.category || 'Uncategorised'}
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 22, color: '#9a9a94', lineHeight: 1 }}>×</div>
        </div>

        {/* Payout blocked banner */}
        {blocked && (
          <div style={{ padding: '12px 22px', borderBottom: '1px solid #fecaca', background: '#fef2f2', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⛔</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#b91c1c' }}>
                {vs === 'rejected' ? 'Payout blocked — verification rejected' : 'Payout blocked — not verified'}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {vs === 'rejected'
                  ? 'A required document was rejected. Creator must re-upload before this payout can be processed.'
                  : 'Creator has not submitted verification documents. Payout should not be processed until verified.'}
              </div>
            </div>
            <span style={{ fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>
              {vs}
            </span>
          </div>
        )}

        {/* Period summary strip */}
        {totals && (
          <div style={{ padding: '14px 22px', borderBottom: '1px solid #e9e9e4', background: '#f0fdf4' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>
              Earnings Summary · {fmt(dateFrom)} – {fmt(dateTo)} · {tips.length} tips · {videoPurchases.length} video unlocks · {passEarnings.length} Kudos Premium payout{passEarnings.length !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                { lbl: 'Gross Earnings', val: money(totals.gross_total), color: '#374151' },
                { lbl: 'Platform Fee',   val: money(totals.fee_total),   color: '#b91c1c' },
                { lbl: 'Net Payout',     val: money(totals.net_total),   color: '#15803d', bold: true },
              ].map(({ lbl, val, color, bold }) => (
                <div key={lbl} style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 7, padding: '8px 10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 8, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 3 }}>{lbl}</div>
                  <div style={{ fontSize: bold ? 13 : 12, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{val}</div>
                </div>
              ))}
            </div>
            {tier && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <TierBadge tier={tier.effective_tier} />
                {tier.discount > 0 ? (
                  <span style={{ fontSize: 10, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>
                    {(tier.discount * 100).toFixed(0)}pp commission discount · {Number(tier.views).toLocaleString()} cumulative views
                  </span>
                ) : (
                  <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                    {Number(tier.views).toLocaleString()} cumulative views — grow views to unlock a commission discount
                  </span>
                )}
                {tier.is_overridden && <span style={{ fontSize: 8, color: '#b45309', fontFamily: "'JetBrains Mono',monospace", background: '#fffbeb', border: '1px solid #fde68a', padding: '1px 5px', borderRadius: 3 }}>ADMIN OVERRIDE</span>}
              </div>
            )}
          </div>
        )}

        {/* Pending request banner + actions */}
        {creator.pending_request_id && (
          <div style={{ padding: '12px 22px', borderBottom: '1px solid #fde68a', background: '#fffbeb', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⏳</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e' }}>Pending payout request — {money(creator.pending_request_amount)}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {PAYOUT_LABELS[creator.pending_payout_method] || creator.pending_payout_method}{creator.pending_payout_method === 'bank' && creator.pending_bank_name ? ` (${creator.pending_bank_name})` : ''} · {creator.pending_payout_account} · requested {fmtTs(creator.pending_requested_at)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button onClick={() => onResolve({ request: { request_id: creator.pending_request_id, amount: creator.pending_request_amount, display_name: creator.display_name, payout_method: creator.pending_payout_method }, status: 'paid' })}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                ✓ Pay
              </button>
              <button onClick={() => onResolve({ request: { request_id: creator.pending_request_id, amount: creator.pending_request_amount, display_name: creator.display_name, payout_method: creator.pending_payout_method }, status: 'rejected' })}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                ✕ Reject
              </button>
            </div>
          </div>
        )}

        {/* Tips list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading…</div>
          ) : tips.length === 0 ? null : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.4fr 1fr 0.9fr 0.9fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                {['', 'Tipper', 'Date', 'Gross', 'Net'].map(h => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                ))}
              </div>
              {tips.map((t, i) => (
                <div key={t.tip_id || i} style={{ display: 'grid', gridTemplateColumns: '0.4fr 1.4fr 1fr 0.9fr 0.9fr', padding: '11px 22px', borderBottom: i < tips.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                  <div style={{ fontSize: 20, lineHeight: 1 }}>{t.emoji || '💜'}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.buyer_name || 'Anonymous'}</div>
                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{t.buyer_phone || '—'}</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTs(t.created_at)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(t.amount)}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(t.net_amount)}</div>
                </div>
              ))}
            </>
          )}

          {/* Video unlocks list */}
          {!loading && videoPurchases.length > 0 && (
            <>
              <div style={{ padding: '10px 22px', fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", background: '#f9f9f7', borderTop: '1px solid #e9e9e4', borderBottom: '1px solid #e9e9e4' }}>
                Video Unlocks
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr 0.9fr 0.9fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                {['Buyer', 'Video', 'Date', 'Gross', 'Net'].map(h => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                ))}
              </div>
              {videoPurchases.map((v, i) => (
                <div key={v.purchase_id || i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr 0.9fr 0.9fr', padding: '11px 22px', borderBottom: i < videoPurchases.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.buyer_name || 'Anonymous'}</div>
                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{v.buyer_phone || '—'}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.video_title}</div>
                  <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTs(v.created_at)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(v.amount)}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(v.net_amount)}</div>
                </div>
              ))}
            </>
          )}

          {/* Pass earnings list */}
          {!loading && passEarnings.length > 0 && (
            <>
              <div style={{ padding: '10px 22px', fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", background: '#f9f9f7', borderTop: '1px solid #e9e9e4', borderBottom: '1px solid #e9e9e4' }}>
                Kudos Premium Earnings (Pooled by Watch-Time)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', padding: '10px 22px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                {['Period', 'Watch Time Share', 'Pool Amount', 'Net'].map(h => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                ))}
              </div>
              {passEarnings.map((p, i) => {
                const sharePct = Number(p.pool_total_seconds) > 0 ? (Number(p.watch_seconds) / Number(p.pool_total_seconds) * 100) : 0
                return (
                  <div key={p.earning_id || i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', padding: '11px 22px', borderBottom: i < passEarnings.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{fmt(p.period_start)} – {fmt(p.period_end)}</div>
                    <div style={{ fontSize: 11, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>{sharePct.toFixed(1)}% · {Number(p.watch_seconds).toLocaleString()}s</div>
                    <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(p.pool_amount)}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(p.net_amount)}</div>
                  </div>
                )
              })}
            </>
          )}

          {!loading && tips.length === 0 && videoPurchases.length === 0 && passEarnings.length === 0 && (
            <div style={{ padding: '48px 22px', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No earnings in this period.</div>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CreatorPayout() {
  const now        = new Date()
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)

  const [tab, setTab] = useState('report')   // 'report' | 'requests'

  // ── Report tab state ──
  const [creators,     setCreators]     = useState([])
  const [repLoading,   setRepLoading]   = useState(true)
  const [repError,     setRepError]     = useState('')
  const [dateFrom,     setDateFrom]     = useState(monthStart)
  const [dateTo,       setDateTo]       = useState(monthEnd)
  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [category,     setCategory]     = useState('')
  const [categories,   setCategories]   = useState([])
  const [verification, setVerification] = useState('')
  const [repPage,       setRepPage]       = useState(1)
  const [repTotalPages, setRepTotalPages] = useState(1)
  const [repTotalCount, setRepTotalCount] = useState(0)
  const [selected,     setSelected]     = useState(null)
  const [exporting,    setExporting]    = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [meta,         setMeta]         = useState({ tip_commission_pct: 0 })
  const [summary,      setSummary]      = useState(null)
  const repFetchRef = useRef(0)

  // ── Requests tab state ──
  const [requests,      setRequests]      = useState([])
  const [reqLoading,    setReqLoading]    = useState(true)
  const [reqError,      setReqError]      = useState('')
  const [reqSearchInput, setReqSearchInput] = useState('')
  const [reqSearch,     setReqSearch]     = useState('')
  const [reqStatus,     setReqStatus]     = useState('')
  const [reqPage,       setReqPage]       = useState(1)
  const [reqTotalPages, setReqTotalPages] = useState(1)
  const [reqTotalCount, setReqTotalCount] = useState(0)
  const [reqStats,      setReqStats]      = useState({ pending_count: 0, pending_amount: 0, paid_count_month: 0, paid_amount_month: 0, rejected_count: 0, total_payable: 0 })
  const [reqExporting,    setReqExporting]    = useState(false)
  const [reqExportingPdf, setReqExportingPdf] = useState(false)
  const reqFetchRef = useRef(0)

  // ── Shared resolve modal ──
  const [resolveAction, setResolveAction] = useState(null)
  const [resolveLoad,   setResolveLoad]   = useState(false)

  const PER_PAGE = 25

  useEffect(() => {
    api.get('/creators/admin/categories')
      .then(res => setCategories((res.data.categories || []).map(c => c.name)))
      .catch(() => setCategories([]))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    const t = setTimeout(() => setReqSearch(reqSearchInput), 400)
    return () => clearTimeout(t)
  }, [reqSearchInput])

  // ── Fetch earnings report ──
  const fetchReport = useCallback(async () => {
    const id = ++repFetchRef.current
    setRepLoading(true); setRepError('')
    try {
      const params = new URLSearchParams({
        page: repPage, limit: PER_PAGE,
        date_from: dateFrom, date_to: dateTo,
        ...(search       && { search }),
        ...(category     && { category }),
        ...(verification && { verification }),
      })
      const res = await api.get(`/creators/admin/payout-report?${params}`)
      if (id !== repFetchRef.current) return
      setCreators(res.data.creators || [])
      setSummary(res.data.summary || null)
      setRepTotalPages(res.data.totalPages || 1)
      setRepTotalCount(res.data.total || 0)
      setMeta(res.data.meta || { tip_commission_pct: 0 })
    } catch {
      if (id !== repFetchRef.current) return
      setRepError('Failed to load creator earnings report.')
    } finally {
      if (id === repFetchRef.current) setRepLoading(false)
    }
  }, [repPage, dateFrom, dateTo, search, category, verification])

  useEffect(() => { fetchReport() },                [fetchReport])
  useEffect(() => { setRepPage(1) }, [dateFrom, dateTo, search, category, verification])

  // ── Fetch payout requests ──
  const fetchRequests = useCallback(async () => {
    const id = ++reqFetchRef.current
    setReqLoading(true); setReqError('')
    try {
      const params = new URLSearchParams({ page: reqPage, limit: PER_PAGE, ...(reqSearch && { search: reqSearch }), ...(reqStatus && { status: reqStatus }) })
      const res = await api.get(`/creators/admin/payout-requests?${params}`)
      if (id !== reqFetchRef.current) return
      setRequests(res.data.requests || [])
      setReqTotalPages(res.data.totalPages || 1)
      setReqTotalCount(res.data.total || 0)
      setReqStats(res.data.stats || { pending_count: 0, pending_amount: 0, paid_count_month: 0, paid_amount_month: 0, rejected_count: 0, total_payable: 0 })
    } catch {
      if (id !== reqFetchRef.current) return
      setReqError('Failed to load creator payout requests.')
    } finally {
      if (id === reqFetchRef.current) setReqLoading(false)
    }
  }, [reqPage, reqSearch, reqStatus])

  useEffect(() => { fetchRequests() }, [fetchRequests])
  useEffect(() => { setReqPage(1) }, [reqSearch, reqStatus])

  const handleExportReportCSV = async () => {
    setExporting(true)
    try {
      const params = new URLSearchParams({ page: 1, limit: 9999, date_from: dateFrom, date_to: dateTo, ...(search && { search }), ...(category && { category }), ...(verification && { verification }) })
      const res = await api.get(`/creators/admin/payout-report?${params}`)
      exportReportCSV(res.data.creators || creators, `${dateFrom}_${dateTo}`)
    } catch {
      exportReportCSV(creators, `${dateFrom}_${dateTo}`)
    } finally { setExporting(false) }
  }

  const handleExportReportPDF = async () => {
    setExportingPdf(true)
    try {
      const params = new URLSearchParams({ page: 1, limit: 9999, date_from: dateFrom, date_to: dateTo, ...(search && { search }), ...(category && { category }), ...(verification && { verification }) })
      const res = await api.get(`/creators/admin/payout-report?${params}`)
      exportReportPDF(res.data.creators || creators, `${dateFrom}_${dateTo}`, meta.tip_commission_pct, { verification, category, search })
    } catch {
      exportReportPDF(creators, `${dateFrom}_${dateTo}`, meta.tip_commission_pct, { verification, category, search })
    } finally { setExportingPdf(false) }
  }

  const handleExportRequestsCSV = async () => {
    setReqExporting(true)
    try {
      const params = new URLSearchParams({ page: 1, limit: 9999, ...(reqSearch && { search: reqSearch }), ...(reqStatus && { status: reqStatus }) })
      const res = await api.get(`/creators/admin/payout-requests?${params}`)
      exportRequestsCSV(res.data.requests || requests, reqStatus)
    } catch {
      exportRequestsCSV(requests, reqStatus)
    } finally { setReqExporting(false) }
  }

  const handleExportRequestsPDF = async () => {
    setReqExportingPdf(true)
    try {
      const params = new URLSearchParams({ page: 1, limit: 9999, ...(reqSearch && { search: reqSearch }), ...(reqStatus && { status: reqStatus }) })
      const res = await api.get(`/creators/admin/payout-requests?${params}`)
      exportRequestsPDF(res.data.requests || requests, reqStatus)
    } catch {
      exportRequestsPDF(requests, reqStatus)
    } finally { setReqExportingPdf(false) }
  }

  const handleResolve = async () => {
    if (!resolveAction) return
    setResolveLoad(true)
    try {
      await api.patch(`/creators/admin/payout-requests/${resolveAction.request.request_id}`, { status: resolveAction.status })
      setResolveAction(null)
      setSelected(null)
      fetchReport()
      fetchRequests()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update payout request')
    } finally { setResolveLoad(false) }
  }

  const periodLabel = `${fmt(dateFrom)} – ${fmt(dateTo)}`

  // Summary totals cover ALL matching creators (not just current page) — from backend
  const sumGross = Number(summary?.gross_total  || 0)
  const sumFee   = Number(summary?.fee_total    || 0)
  const sumNet   = Number(summary?.net_total    || 0)
  const sumTips  = Number(summary?.tips_count   || 0)
  const sumVideos = Number(summary?.video_count || 0)
  const sumPasses = Number(summary?.pass_count  || 0)

  // Page-level aggregates (for table footer)
  const pageGross = creators.reduce((s, r) => s + Number(r.gross_total || 0), 0)
  const pageFee   = creators.reduce((s, r) => s + Number(r.fee_total   || 0), 0)
  const pageNet   = creators.reduce((s, r) => s + Number(r.net_total   || 0), 0)

  const TABS = [
    { id: 'report',   label: '📅 Earnings Report' },
    { id: 'requests', label: '📋 Payout Requests' },
  ]

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Creator Payouts</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Tip earnings per period, plus the payout request queue, for creators (buyers who've gone live without a shop).</div>
        </div>
        {tab === 'report' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {verification && (
              <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4,
                color: VERIF_COLOR[verification] || '#1d4ed8', background: VERIF_BG[verification] || '#dbeafe',
                border: `1px solid ${VERIF_BORDER[verification] || '#93c5fd'}`, whiteSpace: 'nowrap' }}>
                {{'verified':'✓ Verified','pending':'⏳ Pending','unverified':'⚠ Unverified','rejected':'✕ Rejected','blocked':'⛔ Blocked'}[verification]}
              </span>
            )}
            <button onClick={handleExportReportCSV} disabled={exporting || creators.length === 0}
              style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: exporting ? '#bfdbfe' : '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(29,78,216,0.18)' }}>
              {exporting ? '⏳…' : '⬇ CSV'}
            </button>
            <button onClick={handleExportReportPDF} disabled={exportingPdf || creators.length === 0}
              style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: exportingPdf ? '#e9d5ff' : '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: exportingPdf ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(124,58,237,0.2)' }}>
              {exportingPdf ? '⏳…' : '⬇ PDF'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={handleExportRequestsCSV} disabled={reqExporting || requests.length === 0}
              style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: reqExporting ? '#bfdbfe' : '#1d4ed8', color: '#fff', fontSize: 12, fontWeight: 700, cursor: reqExporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(29,78,216,0.18)' }}>
              {reqExporting ? '⏳…' : '⬇ CSV'}
            </button>
            <button onClick={handleExportRequestsPDF} disabled={reqExportingPdf || requests.length === 0}
              style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: reqExportingPdf ? '#e9d5ff' : '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, cursor: reqExportingPdf ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(124,58,237,0.2)' }}>
              {reqExportingPdf ? '⏳…' : '⬇ PDF'}
            </button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: 5, gap: 2, marginBottom: 20, width: 'fit-content' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: tab === t.id ? '#111317' : 'transparent', color: tab === t.id ? '#fff' : '#9a9a94', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════ EARNINGS REPORT TAB ══════════════════ */}
      {tab === 'report' && (
        <>
          {/* Filters row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>From</span>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>To</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
            </div>

            {/* Month shortcuts */}
            {[0, 1, 2].map(monthsAgo => {
              const d    = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1)
              const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`
              const to   = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().slice(0,10)
              const lbl  = monthsAgo === 0 ? 'This month' : d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })
              const active = dateFrom === from && dateTo === to
              return (
                <button key={monthsAgo} onClick={() => { setDateFrom(from); setDateTo(to) }}
                  style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {lbl}
                </button>
              )
            })}

            <div style={{ flex: 1 }} />

            {/* Search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 11px' }}>
              <span style={{ fontSize: 13, color: '#9a9a94' }}>🔍</span>
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Creator name, owner, phone…"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, width: 170, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
              {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 14 }}>×</span>}
            </div>

            {/* Category filter */}
            <select value={category} onChange={e => setCategory(e.target.value)}
              style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 11px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            {/* Verification filter */}
            <select value={verification} onChange={e => setVerification(e.target.value)}
              style={{ border: `1px solid ${verification ? (verification === 'verified' ? '#bbf7d0' : verification === 'blocked' || verification === 'rejected' ? '#fecaca' : '#bfdbfe') : '#e9e9e4'}`, borderRadius: 7, padding: '8px 11px', fontSize: 12, fontWeight: 600, color: verification ? (verification === 'verified' ? '#15803d' : verification === 'blocked' || verification === 'rejected' ? '#b91c1c' : '#1d4ed8') : '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              <option value="">All Verification</option>
              <option value="verified">✓ Verified</option>
              <option value="pending">⏳ Pending Review</option>
              <option value="unverified">⚠ Unverified (no docs)</option>
              <option value="rejected">✕ Rejected</option>
              <option value="blocked">⛔ Blocked (Unverified + Rejected)</option>
            </select>
          </div>

          {/* Period label */}
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: (search || category || verification) ? 10 : 16 }}>
            Report period: <b style={{ color: '#111317' }}>{periodLabel}</b> · {repTotalCount.toLocaleString()} creators · {sumTips.toLocaleString()} tips · {sumVideos.toLocaleString()} video unlocks · {sumPasses.toLocaleString()} Kudos Premium payouts
            {meta.tip_commission_pct > 0 && <> · Tip commission {(meta.tip_commission_pct * 100).toFixed(0)}% base · <span style={{ color: '#b45309' }}>tier discounts applied per-row</span></>}
          </div>

          {/* Filter active banner */}
          {(search || category || verification) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, background: verification === 'blocked' || verification === 'rejected' ? '#fef2f2' : '#eff6ff', border: `1px solid ${verification === 'blocked' || verification === 'rejected' ? '#fecaca' : '#bfdbfe'}`, borderRadius: 8, padding: '9px 14px' }}>
              <span style={{ fontSize: 13 }}>{verification === 'blocked' || verification === 'rejected' ? '⛔' : verification === 'verified' ? '✓' : '🔍'}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: verification === 'blocked' || verification === 'rejected' ? '#b91c1c' : '#1d4ed8' }}>Filtered results</span>
                {search && <span style={{ fontSize: 12, color: '#374151' }}> · "{search}"</span>}
                {category && <span style={{ fontSize: 12, color: '#374151' }}> · {category}</span>}
                {verification && <span style={{ fontSize: 12, fontWeight: 700, color: verification === 'blocked' || verification === 'rejected' ? '#b91c1c' : verification === 'verified' ? '#15803d' : '#1d4ed8' }}>
                  {' · '}{{'verified':'✓ Verified','pending':'⏳ Pending','unverified':'⚠ Unverified','rejected':'✕ Rejected','blocked':'⛔ Payout Blocked'}[verification]}
                </span>}
                <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6 }}>— CSV and PDF will reflect this filter</span>
              </div>
              <button onClick={() => { setSearchInput(''); setSearch(''); setCategory(''); setVerification('') }}
                style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>
                Clear all ×
              </button>
            </div>
          )}

          {/* Summary cards — totals cover ALL matching creators (all pages) */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <SummaryCard label="Gross Earnings"    value={money(sumGross)} accent="#374151" sub={`${sumTips.toLocaleString()} tips · ${sumVideos.toLocaleString()} video unlocks · ${sumPasses.toLocaleString()} Kudos Premium payouts · ${repTotalCount} creators`} />
            <SummaryCard label="Platform Fee"      value={money(sumFee)}   accent="#b91c1c" sub="kept by the platform, tier-discounted" />
            <SummaryCard label="Net Payout"        value={money(sumNet)}   accent="#15803d" bold sub="amount owed to creators for this period" />
          </div>

          {repError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {repError}</div>
          )}

          {/* Main table */}
          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.1fr 0.6fr 0.6fr 1.1fr 1.1fr 1.2fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
              {['Creator', 'Category', 'Owner', 'Tips', 'Premium', 'Gross', 'Platform Fee', 'Net Payout'].map(h => (
                <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
              ))}
            </div>

            {repLoading ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading creator earnings report…</div>
            ) : creators.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No creators earned anything in this period.</div>
            ) : (
              <>
                {creators.map((c, i) => {
                  const bg = avatarBg(c.display_name || '')
                  const vs = c.verification_status ?? 'unverified'
                  const blocked = ['unverified', 'rejected'].includes(vs)
                  return (
                    <div key={c.creator_id || i}
                      onClick={() => setSelected(c)}
                      style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.1fr 0.6fr 0.6fr 1.1fr 1.1fr 1.2fr', padding: '12px 18px', borderBottom: i < creators.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{initials(c.display_name)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.display_name || '—'}</div>
                          <div style={{ fontSize: 10, color: '#9a9a94' }}>{c.owner_phone || '—'}</div>
                          {blocked && (
                            <span style={{ display: 'inline-block', marginTop: 4, fontSize: 8, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: '1px 5px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                              ⛔ {vs === 'rejected' ? 'Rejected' : 'Unverified'}
                            </span>
                          )}
                          {c.pending_request_id && (
                            <span style={{ display: 'inline-block', marginTop: 4, marginLeft: blocked ? 5 : 0, fontSize: 8, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', padding: '1px 5px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                              ⏳ Requested {moneyN(c.pending_request_amount)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div>
                        <span style={{ display: 'inline-block', background: '#f3f4f6', color: '#374151', fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'JetBrains Mono',monospace" }}>
                          {c.category || '—'}
                        </span>
                      </div>

                      <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.owner_name || '—'}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{c.tips_count}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{c.pass_count || 0}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(c.gross_total)}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>{moneyN(c.fee_total)}</div>
                      <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(c.net_total)}</div>
                    </div>
                  )
                })}

                {/* Page footer totals */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.1fr 0.6fr 0.6fr 1.1fr 1.1fr 1.2fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Page {repPage} of {repTotalPages}</div>
                    <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>{creators.length} creators shown</div>
                  </div>
                  <div /><div />
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{creators.reduce((s, r) => s + Number(r.tips_count || 0), 0)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{creators.reduce((s, r) => s + Number(r.pass_count || 0), 0)}</div>
                  <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(pageGross)}</div>
                  <div style={{ fontSize: 13, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>{moneyN(pageFee)}</div>
                  <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(pageNet)}</div>
                </div>
              </>
            )}
          </div>

          {/* Pagination */}
          {repTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                Page {repPage} of {repTotalPages} · {repTotalCount.toLocaleString()} creators
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <PagBtn label="← Prev" disabled={repPage === 1}              onClick={() => setRepPage(p => p - 1)} />
                {Array.from({ length: Math.min(5, repTotalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(repPage - 2, repTotalPages - 4)) + i
                  return <PagBtn key={p} label={p} active={p === repPage} onClick={() => setRepPage(p)} />
                })}
                <PagBtn label="Next →" disabled={repPage === repTotalPages} onClick={() => setRepPage(p => p + 1)} />
              </div>
            </div>
          )}

          {/* Earnings drawer */}
          {selected && (
            <CreatorReportDrawer
              creator={selected}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onClose={() => setSelected(null)}
              onResolve={setResolveAction}
            />
          )}
        </>
      )}

      {/* ══════════════════ PAYOUT REQUESTS TAB ══════════════════ */}
      {tab === 'requests' && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <SummaryCard label="Pending Requests" value={reqStats.pending_count} accent="#b45309" sub={money(reqStats.pending_amount)} />
            <SummaryCard label="Paid This Month"  value={reqStats.paid_count_month} accent="#15803d" sub={money(reqStats.paid_amount_month)} />
            <SummaryCard label="Rejected"         value={reqStats.rejected_count} accent="#b91c1c" />
            <SummaryCard label="Unclaimed Payable Balance" value={money(reqStats.total_payable)} accent="#e8440a" bold
              sub="across all creators — not yet requested" />
          </div>

          {/* Filters row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 11px', flex: 1, minWidth: 200 }}>
              <span style={{ fontSize: 13, color: '#9a9a94' }}>🔍</span>
              <input value={reqSearchInput} onChange={e => setReqSearchInput(e.target.value)} placeholder="Creator name, owner, phone…"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
              {reqSearchInput && <span onClick={() => { setReqSearchInput(''); setReqSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 14 }}>×</span>}
            </div>
            <select value={reqStatus} onChange={e => setReqStatus(e.target.value)}
              style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 11px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              <option value="">All Statuses</option>
              <option value="pending">⏳ Pending</option>
              <option value="paid">✓ Paid</option>
              <option value="rejected">✕ Rejected</option>
            </select>
          </div>

          {reqError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {reqError}</div>
          )}

          {/* Table */}
          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr 0.9fr 1.1fr 1.4fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
              {['Creator', 'Method', 'Account', 'Amount', 'Status', 'Requested', 'Actions'].map(h => (
                <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
              ))}
            </div>

            {reqLoading ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading payout requests…</div>
            ) : requests.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No payout requests found.</div>
            ) : (
              requests.map((r, i) => {
                const s  = REQ_STATUS[r.status] || REQ_STATUS.pending
                const bg = avatarBg(r.display_name || '')
                return (
                  <div key={r.request_id}
                    style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr 0.9fr 1.1fr 1.4fr', padding: '12px 18px', borderBottom: i < requests.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{initials(r.display_name)}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.display_name || '—'}</div>
                        <div style={{ fontSize: 10, color: '#9a9a94' }}>{r.owner_name || r.owner_phone || '—'}</div>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: '#374151' }}>{PAYOUT_LABELS[r.payout_method] || r.payout_method || '—'}</div>
                      {r.payout_method === 'bank' && r.bank_name && (
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{r.bank_name}</div>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.payout_account || '—'}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{moneyN(r.amount)}</div>

                    <div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>
                        {s.label}
                      </span>
                    </div>

                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(r.requested_at)}</div>

                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.status === 'pending' ? (
                        <>
                          <button onClick={() => setResolveAction({ request: r, status: 'paid' })}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                            ✓ Mark Paid
                          </button>
                          <button onClick={() => setResolveAction({ request: r, status: 'rejected' })}
                            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                            ✕ Reject
                          </button>
                        </>
                      ) : (
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{r.resolved_at ? `Resolved ${fmtTs(r.resolved_at)}` : '—'}</div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Pagination */}
          {reqTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                Page {reqPage} of {reqTotalPages} · {reqTotalCount.toLocaleString()} requests
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <PagBtn label="← Prev" disabled={reqPage === 1}              onClick={() => setReqPage(p => p - 1)} />
                {Array.from({ length: Math.min(5, reqTotalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(reqPage - 2, reqTotalPages - 4)) + i
                  return <PagBtn key={p} label={p} active={p === reqPage} onClick={() => setReqPage(p)} />
                })}
                <PagBtn label="Next →" disabled={reqPage === reqTotalPages} onClick={() => setReqPage(p => p + 1)} />
              </div>
            </div>
          )}
        </>
      )}

      <ResolveModal action={resolveAction} onClose={() => setResolveAction(null)} onConfirm={handleResolve} loading={resolveLoad} />
    </div>
  )
}
